/**
 * The `/` index gatekeeper (gate-panel plan, decision 5): before the dashboard renders, route the
 * user to the panel this device should open — straight to `/gate` for a gate-only user or a
 * remembered gate choice, to `/choose-panel` when both panels are usable and the device has no
 * remembered choice, and through to the wrapped dashboard otherwise. Declarative `<Navigate>` in
 * render (R-18.181), same as the other route guards.
 */

import { Navigate } from 'react-router-dom';

import type { ReactNode } from 'react';

import { useAuth } from '@/shared/auth/AuthProvider';
import { resolvePanelTarget, usePanelStore } from '@/shared/stores/panel-store';
import { canUseMainPanel } from '@/shell/AppLayout';

export function PanelRedirect({ children }: { children: ReactNode }): React.JSX.Element {
  const { hasCapability } = useAuth();
  const panel = usePanelStore((s) => s.panel);

  const target = resolvePanelTarget({
    canUseMain: canUseMainPanel(hasCapability),
    canUseGate: hasCapability('gate.access'),
    storedPanel: panel,
  });
  if (target !== '/') {
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
}
